// DashboardScreen (TEMPLATES.md): a widget grid. One column on phone, two
// on tablet, three on desktop. Each widget names its column, unless the
// person has arranged the page (`ordered`, L3, B-247): then the columns fill
// from the order, the first ones on the left. Drawn through the kit's classes
// (LOOKS.md Part 7): no inline layout.

import type { ReactNode } from 'react';
import { useTier } from '../shell/useTier';

export interface Widget {
  id: string;
  content: ReactNode;
  /** Column on tablet/desktop; phone ignores it. */
  column?: 1 | 2 | 3;
  /** Rendered pinned above the grid (lineup health). */
  pinned?: boolean;
  /**
   * Left out entirely, for a summary the detail level has switched off. Not
   * CSS-hidden: the accessibility tree should match the screen, and every
   * widget's own screen is still in the rail.
   */
  hidden?: boolean;
}

export function DashboardScreen({
  widgets,
  ordered = false,
  footer = null,
}: {
  widgets: Widget[];
  /**
   * The widgets are in the person's order: the columns fill from it, an even
   * share each and the first ones on the left, and `column` is ignored. One
   * order for every width, because a page in a different order on the phone
   * than on the computer is a puzzle (PLAN-2026-10-02 §6).
   */
  ordered?: boolean;
  /** At the foot of the page, across every column: the League page's own controls. */
  footer?: ReactNode;
}) {
  const tier = useTier();
  const columns = tier === 'phone' ? 1 : tier === 'tablet' ? 2 : 3;
  const shown = widgets.filter((w) => !w.hidden);
  const pinned = shown.filter((w) => w.pinned);
  const flow = shown.filter((w) => !w.pinned);

  const cols: Widget[][] = Array.from({ length: columns }, () => []);
  if (ordered) {
    // Contiguous shares, the earlier columns taking the remainder.
    let at = 0;
    for (let c = 0; c < columns; c += 1) {
      const share = Math.floor(flow.length / columns) + (c < flow.length % columns ? 1 : 0);
      cols[c]!.push(...flow.slice(at, at + share));
      at += share;
    }
  } else {
    flow.forEach((w, i) => {
      const target = columns === 1 ? 0 : Math.min(columns - 1, (w.column ?? (i % columns) + 1) - 1);
      cols[target]!.push(w);
    });
  }

  return (
    <div className="fsp-screen">
      {pinned.length ? (
        <div className="fsp-pinned fsp-widgets-pinned">
          {pinned.map((w) => (
            <div key={w.id}>{w.content}</div>
          ))}
        </div>
      ) : null}
      <div className={`fsp-widgets fsp-widgets-${columns}`}>
        {cols.map((col, i) => (
          <div key={i} className="fsp-widget-column">
            {col.map((w) => (
              <div key={w.id} className="fsp-widget">
                {w.content}
              </div>
            ))}
          </div>
        ))}
        {/* Inside the grid, so it scrolls with the page and sits at its foot. */}
        {footer !== null ? <div className="fsp-widgets-footer">{footer}</div> : null}
      </div>
    </div>
  );
}
